import React, { Component, useEffect } from 'react';

import { useSnackbar } from 'notistack';

import { Stack } from '@mui/material';

import { Bedbank } from '@luxuryescapes/contract-svc-bedbank';

import Spinner from '~/components/Common/Spinner';

import bedbankService from '~/services/BedbankService';

import { addQuery, parseSearchString } from '~/utils/url';

import PropertiesTable from './PropertiesTable';
import PullContentForm from './PullContent';
import PullPropertiesTable from './PullPropertiesTable';
import PropertiesSearchForm from './SearchForm';
import { CoreQuery, FilterQuery, Supplier } from './type';

interface Props {
  history: any;
  location: any;
}

interface State {
  isLoading: boolean;
  isPulling: boolean;
  count: number;
  data: Array<Bedbank.ListSupplierProperty>;
  pulledData: Array<Bedbank.ListSupplierProperty>;
  errorMessage: string | null;
}

const DEFAULT_SIZE_PER_PAGE = 15;

function PageContainerNotify(props: any) {
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (props.message) {
      enqueueSnackbar(props.message, { variant: 'error' });
    }
  }, [props.message, enqueueSnackbar]);

  return null;
}

class PageContainer extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      isLoading: true,
      isPulling: false,
      data: [],
      pulledData: [],
      count: 0,
      errorMessage: null,
    };
  }

  fetchData(data: CoreQuery) {
    this.setState({ isLoading: true });
    bedbankService
      .getSuppliersProperties(data)
      .then((data) => {
        this.setState({
          data: data.result,
          count: data.count,
          isLoading: false,
          errorMessage: null,
        });
      })
      .catch((error) => {
        this.setState({
          isLoading: false,
          errorMessage: error?.message || 'Failed to fetch supplier properties',
        });
      });
  }

  queries(): Partial<CoreQuery> {
    const { location } = this.props;
    return parseSearchString(location.search);
  }

  currentPage() {
    return Number(this.queries().page || 1);
  }

  currentLimit() {
    return Number(this.queries().limit || DEFAULT_SIZE_PER_PAGE);
  }

  currentOrderBy() {
    return this.queries().orderBy || 'asc';
  }

  currentSortBy() {
    return this.queries().sortBy || 'hotelName';
  }

  currentFilterByName() {
    return this.queries().filterByName;
  }

  currentFilterBySupplier() {
    return this.queries().filterBySupplier;
  }

  currentFilterById() {
    return this.queries().filterById;
  }

  getQueryData(): CoreQuery {
    return {
      page: this.currentPage(),
      limit: this.currentLimit(),
      orderBy: this.currentOrderBy(),
      sortBy: this.currentSortBy(),
      filterByName: this.currentFilterByName(),
      filterBySupplier: this.currentFilterBySupplier(),
      filterById: this.currentFilterById(),
    };
  }

  componentDidMount() {
    this.fetchData(this.getQueryData());
  }

  onPageChange = (page: number, limit: number) => {
    const { history, location } = this.props;
    const query = this.getQueryData();
    const newQuery = {
      ...query,
      page,
      limit,
    };
    const newLocation = addQuery(location, newQuery);
    history.push(newLocation);

    this.fetchData(newQuery);
  };

  onSortChange = (orderBy: CoreQuery['orderBy'], sortBy: CoreQuery['sortBy']) => {
    const { history, location } = this.props;
    const query = this.getQueryData();
    const newQuery = {
      ...query,
      orderBy,
      sortBy,
    };
    const newLocation = addQuery(location, newQuery);
    history.push(newLocation);

    this.fetchData(newQuery);
  };

  handleSearchSubmit = (query?: Partial<FilterQuery>) => {
    const { history, location } = this.props;
    const allQuery = this.getQueryData();
    const newQuery = {
      ...allQuery,
      filterByName: query?.filterByName,
      filterBySupplier: query?.filterBySupplier,
      filterById: query?.filterById,
    };
    const newLocation = addQuery(location, newQuery);
    history.push(newLocation);

    this.fetchData(newQuery);
  };

  handlePullSubmit = (supplier: Supplier, externalId: string) => {
    this.setState({ isPulling: true });
    bedbankService
      .pullSupplierPropertyContent({
        externalId,
        supplier,
      })
      .then((data) => {
        this.setState({
          pulledData: data.result,
          isPulling: false,
          errorMessage: null,
        });
      })
      .catch((error) => {
        this.setState({
          isPulling: false,
          errorMessage: error?.message || 'Failed to fetch supplier content',
        });
      });
  };

  render() {
    return (
      <Stack direction="column" spacing={2}>
        {this.state.errorMessage && <PageContainerNotify message={this.state.errorMessage} />}
        <PullContentForm handleSubmit={this.handlePullSubmit} isPulling={this.state.isPulling} />
        {this.state.pulledData.length > 0 && <PullPropertiesTable data={this.state.pulledData} />}
        <PropertiesSearchForm queries={this.getQueryData()} handleSubmit={this.handleSearchSubmit} />
        {this.state.isLoading && <Spinner />}
        {!this.state.isLoading && (
          <PropertiesTable
            data={this.state.data}
            count={this.state.count}
            onPageChange={this.onPageChange}
            onSortChange={this.onSortChange}
            queries={this.getQueryData()}
          />
        )}
      </Stack>
    );
  }
}

export default PageContainer;
